import { db } from "./firebase";
import { doc, getDoc, setDoc, getDocs, collection, query, where, deleteDoc, serverTimestamp } from "firebase/firestore";

// Global MTA BusTime API key
export const BUS_API_KEY = "bb46e5ab-707a-4178-8f8b-388e766ef44b";

// Default config matching current hardcoded values
export const DEFAULT_CONFIG = {
    subwayStopId: "",
    subwayRoutes: [],
    busConfigs: [],
    transfers: [],
};
// Seed admin UIDs — auto-created as admin on first login
const SEED_ADMIN_UIDS = [
    "CMvNU71TzjQaxtWSerr29MEqjHt1",
];

// Get user record from Firestore, auto-seed admins
export async function getUserRole(uid) {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) return snap.data();

    // Auto-seed admin if in the seed list
    if (SEED_ADMIN_UIDS.includes(uid)) {
        const adminDoc = { role: "admin", status: "approved", createdAt: serverTimestamp() };
        await setDoc(doc(db, "users", uid), adminDoc);
        return adminDoc;
    }
    return null; // no record
}

export function isAdmin(uid) {
    return SEED_ADMIN_UIDS.includes(uid);
}

// Request access (creates a pending user doc)
export async function requestAccess(user) {
    await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        displayName: user.displayName,
        role: "user",
        status: "pending",
        requestedAt: serverTimestamp(),
    });
}

// Admin: approve a pending user
export async function approveUser(uid) {
    await setDoc(doc(db, "users", uid), {
        status: "approved",
        role: "user",
        approvedAt: serverTimestamp(),
    }, { merge: true });
}

// Admin: deny/remove a user
export async function denyUser(uid) {
    await deleteDoc(doc(db, "users", uid));
}

// Admin: get all pending access requests
export async function getPendingRequests() {
    const q = query(collection(db, "users"), where("status", "==", "pending"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
}

// Admin: get all pages (every page in the system)
export async function getAllPages() {
    const snap = await getDocs(collection(db, "pages"));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Admin: get all approved users
export async function getAllApprovedUsers() {
    const q = query(collection(db, "users"), where("status", "==", "approved"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
}

// Admin: set a user's role (admin or user)
export async function setUserRole(uid, role) {
    await setDoc(doc(db, "users", uid), { role }, { merge: true });
}

// Firestore helpers
export async function getPageConfig(pageId) {
    const snap = await getDoc(doc(db, "pages", pageId));
    if (!snap.exists()) return null;
    return snap.data();
}

export async function savePageConfig(pageId, config, ownerId) {
    await setDoc(doc(db, "pages", pageId), {
        ...config,
        ownerId,
        updatedAt: serverTimestamp(),
    }, { merge: true });
}

export async function createPage(pageId, ownerId) {
    const existing = await getDoc(doc(db, "pages", pageId));
    if (existing.exists()) {
        throw new Error("Page ID already taken");
    }
    await setDoc(doc(db, "pages", pageId), {
        ...DEFAULT_CONFIG,
        ownerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
}

export async function deletePage(pageId) {
    await deleteDoc(doc(db, "pages", pageId));
}

export async function getUserPages(uid) {
    const q = query(collection(db, "pages"), where("ownerId", "==", uid));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ===== Subway Stops Cache =====
const STOPS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export async function getCachedStops(systemId, fetchFn) {
    const cacheDocId = `stops_${systemId}`;
    try {
        const snap = await getDoc(doc(db, "cache", cacheDocId));
        if (snap.exists()) {
            const data = snap.data();
            const age = Date.now() - (data.updatedAt?.toMillis?.() || 0);
            if (age < STOPS_CACHE_TTL && data.stops?.length > 0) {

                return data.stops;
            }
        }
    } catch (e) {
        // silent fallback to API
    }

    // Cache miss or expired — fetch from API

    const result = await fetchFn(systemId);
    const stops = result.stops || [];

    // Write to cache (fire and forget)
    if (stops.length > 0) {
        const simplified = stops.map(s => ({ id: s.id, name: s.name }));
        setDoc(doc(db, "cache", cacheDocId), {
            stops: simplified,
            updatedAt: serverTimestamp(),
            count: simplified.length,
        }).catch(() => { });
    }

    return stops;
}
